import { parseApiDateUtc } from '@/lib/api-date'
import { getUserTimeZone } from '@/lib/user-timezone'

/** График в интерфейсе задаётся в МСК; пересчёт отображения в локальный TZ. */
export const SCHEDULE_TZ_MSK = 'Europe/Moscow'

export type WorkInterval = { start_time: string; end_time: string }
export type BreakInterval = { start_time: string; end_time: string }

export type ScheduleEntry = {
  id: number
  user_id: string
  date: string
  work_intervals?: WorkInterval[]
  break_intervals?: BreakInterval[]
  break_start?: string | null
  break_end?: string | null
  start_time?: string
  end_time?: string
}

export type ShiftSegment = { start: Date; end: Date }

const MINUTES_PER_DAY = 24 * 60

export function padTimeStr(str: string): string {
  if (!str || typeof str !== 'string') return '00:00'
  const parts = str.trim().substring(0, 5).split(':')
  const h = parseInt(parts[0], 10)
  const m = parseInt(parts[1], 10) || 0
  if (Number.isNaN(h)) return '00:00'
  return `${String(h).padStart(2, '0')}:${String(Number.isNaN(m) ? 0 : m).padStart(2, '0')}`
}

/** Момент `dateStr` + `timeStr` (часы:минуты) в МСК → UTC ms. Россия: фиксированный UTC+3. */
export function mskWallTimeToUtcMs(dateStr: string, timeStr: string): number {
  const s = padTimeStr(timeStr)
  return new Date(`${dateStr}T${s}:00+03:00`).getTime()
}

export function mskTimeToTz(dateStr: string, timeStr: string, targetTz: string): string {
  const s = padTimeStr(timeStr)
  try {
    const ms = mskWallTimeToUtcMs(dateStr, s)
    if (Number.isNaN(ms)) return s
    const d = new Date(ms)
    const formatted = d.toLocaleTimeString('ru-RU', {
      timeZone: targetTz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    return padTimeStr(formatted.replace('.', ':'))
  } catch {
    return s
  }
}

export function getNowInMsk(): string {
  try {
    const s = new Intl.DateTimeFormat('ru-RU', {
      timeZone: SCHEDULE_TZ_MSK,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date())
    return padTimeStr(s.replace('.', ':'))
  } catch {
    try {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: SCHEDULE_TZ_MSK,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).formatToParts(new Date())
      const h = parts.find((p) => p.type === 'hour')?.value ?? '00'
      const m = parts.find((p) => p.type === 'minute')?.value ?? '00'
      return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`
    } catch {
      return '00:00'
    }
  }
}

export function todayStrInMsk(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: SCHEDULE_TZ_MSK })
}

export function timeToMinutes(t: string): number {
  if (!t || typeof t !== 'string') return 0
  const s = t.trim().substring(0, 5)
  const [h, m] = s.split(':').map(Number)
  if (Number.isNaN(h)) return 0
  return ((h || 0) * 60 + (Number.isNaN(m) ? 0 : m)) % MINUTES_PER_DAY
}

export function intervalMinutes(startMin: number, endMin: number): number {
  if (endMin > startMin) return endMin - startMin
  return MINUTES_PER_DAY - startMin + endMin
}

export function intervalEndForPlanDay(startMin: number, endMin: number): number {
  return endMin <= startMin ? endMin + MINUTES_PER_DAY : endMin
}

export function formatHoursFromMinutes(minutes: number): string {
  const value = Math.max(0, minutes)
  if (value <= 0) return '0 ч'
  const h = Math.floor(value / 60)
  const m = value % 60
  if (m === 0) return `${h} ч`
  if (h === 0) return `${m} мин`
  return `${h} ч ${m} мин`
}

export function getWorkIntervals(s: ScheduleEntry | undefined): WorkInterval[] {
  if (!s) return []
  if (Array.isArray(s.work_intervals) && s.work_intervals.length) return s.work_intervals
  if (s.start_time && s.end_time) {
    return [{ start_time: s.start_time.substring(0, 5), end_time: s.end_time.substring(0, 5) }]
  }
  return []
}

export function getBreakIntervals(s: ScheduleEntry | undefined): BreakInterval[] {
  if (!s) return []
  if (Array.isArray(s.break_intervals) && s.break_intervals.length) {
    return s.break_intervals.map((b) => ({
      start_time: (b.start_time || (b as { start?: string }).start || '').substring(0, 5),
      end_time: (b.end_time || (b as { end?: string }).end || '').substring(0, 5),
    }))
  }
  if (s.break_start && s.break_end) {
    return [{ start_time: String(s.break_start).substring(0, 5), end_time: String(s.break_end).substring(0, 5) }]
  }
  return []
}

export function breakOverlapMinutes(breakStartMin: number, breakEndMin: number, workIntervals: WorkInterval[]): number {
  if (breakEndMin <= breakStartMin || !workIntervals.length) return 0
  let overlap = 0
  for (const i of workIntervals) {
    const start = timeToMinutes(i.start_time)
    const end = timeToMinutes(i.end_time)
    if (end > start) {
      const overlapStart = Math.max(breakStartMin, start)
      const overlapEnd = Math.min(breakEndMin, end)
      if (overlapEnd > overlapStart) overlap += overlapEnd - overlapStart
    } else {
      const overlap1 = Math.max(0, Math.min(breakEndMin, MINUTES_PER_DAY) - Math.max(breakStartMin, start))
      const overlap2 = Math.max(0, Math.min(breakEndMin, end) - Math.max(breakStartMin, 0))
      overlap += overlap1 + overlap2
    }
  }
  return overlap
}

export function scheduleDayWorkingMinutes(schedule: ScheduleEntry | undefined): number {
  if (!schedule) return 0
  const workIntervals = getWorkIntervals(schedule)
  let total = 0
  for (const i of workIntervals) {
    const start = timeToMinutes(i.start_time)
    const end = timeToMinutes(i.end_time)
    total += intervalMinutes(start, end)
  }
  const breaks = getBreakIntervals(schedule)
  for (const b of breaks) {
    const bs = timeToMinutes(b.start_time)
    const be = timeToMinutes(b.end_time)
    total -= breakOverlapMinutes(bs, be, workIntervals)
  }
  return Math.max(0, total)
}

export function userMonthWorkingHours(
  userId: string,
  year: number,
  month: number,
  byUser: Record<string, Record<string, ScheduleEntry>>,
): number {
  const userSchedules = byUser[userId] || {}
  const lastDay = new Date(year, month, 0).getDate()
  let totalMinutes = 0
  for (let day = 1; day <= lastDay; day++) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    totalMinutes += scheduleDayWorkingMinutes(userSchedules[dateStr])
  }
  return Math.round(totalMinutes / 6) / 10
}

export function formatIntervalLine(s: ScheduleEntry | undefined, dateStr: string, useLocalTz: boolean): string {
  const ivs = getWorkIntervals(s)
  if (!ivs.length) return ''
  const userTz = getUserTimeZone()
  return ivs
    .map((i) => {
      const st = i.start_time?.substring(0, 5) || ''
      const et = i.end_time?.substring(0, 5) || ''
      if (useLocalTz && dateStr) {
        return `${mskTimeToTz(dateStr, st, userTz)}–${mskTimeToTz(dateStr, et, userTz)}`
      }
      return `${st}–${et}`
    })
    .join(', ')
}

export function formatBreakLine(s: ScheduleEntry | undefined, dateStr: string, useLocalTz: boolean): string {
  const br = getBreakIntervals(s)
  if (!br.length) return ''
  const userTz = getUserTimeZone()
  return br
    .map((b) => {
      const bs = b.start_time.substring(0, 5)
      const be = b.end_time.substring(0, 5)
      if (useLocalTz && dateStr) {
        return `${mskTimeToTz(dateStr, bs, userTz)}–${mskTimeToTz(dateStr, be, userTz)}`
      }
      return `${bs}–${be}`
    })
    .join(', ')
}

/** Подпись начала/конца фактического интервала: МСК или часовой пояс пользователя. */
export function formatShiftSegmentRange(seg: ShiftSegment, useLocalTz: boolean): string {
  const tz = useLocalTz ? getUserTimeZone() : SCHEDULE_TZ_MSK
  const fmt = (d: Date) => {
    const s = d.toLocaleTimeString('ru-RU', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    return padTimeStr(s.replace('.', ':'))
  }
  return `${fmt(seg.start)}–${fmt(seg.end)}`
}

/** Минуты от полуночи МСК на календарный день `dateStr` (YYYY-MM-DD), попадание отрезка UTC. */
export function utcSegmentToMskMinutesOnCalendarDate(dateStr: string, seg: ShiftSegment): {
  start: number
  end: number
} | null {
  const dayStart = new Date(`${dateStr}T00:00:00+03:00`).getTime()
  const dayEnd = dayStart + 24 * 60 * 60 * 1000
  const a = Math.max(seg.start.getTime(), dayStart)
  const b = Math.min(seg.end.getTime(), dayEnd)
  if (b <= a) return null
  return { start: (a - dayStart) / 60000, end: (b - dayStart) / 60000 }
}

export function totalSegmentMinutes(segments: ShiftSegment[]): number {
  return segments.reduce((sum, seg) => {
    const minutes = Math.max(0, Math.round((seg.end.getTime() - seg.start.getTime()) / 60_000))
    return sum + minutes
  }, 0)
}

export function actualWorkedMinutesForDate(
  shiftSegmentsForDate: ShiftSegment[],
  breakSegmentsForDate: ShiftSegment[] = [],
  breakMinutesPerPlannedHour = 0,
): number {
  const raw = totalSegmentMinutes(shiftSegmentsForDate)
  const breakUsed = totalSegmentMinutes(breakSegmentsForDate)
  if (breakUsed <= 0 || !breakMinutesPerPlannedHour) {
    return Math.max(0, raw - breakUsed)
  }
  // Перерыв оплачивается, но не больше, чем фактически отработанные в этот день
  // часы * минут/час — превышение (если отработано меньше нормы) не оплачивается.
  const paidBreakCap = (raw / 60) * breakMinutesPerPlannedHour
  const unpaidBreak = Math.max(0, breakUsed - paidBreakCap)
  return Math.max(0, raw - unpaidBreak)
}

export function isTimeInInterval(nowTime: string, start: string, end: string): boolean {
  const s = (start || '').substring(0, 5)
  const e = (end || '').substring(0, 5)
  return !!(s && e && nowTime >= s && nowTime <= e)
}

function mskDateKeyFromUtc(d: Date): string {
  return d.toLocaleDateString('en-CA', { timeZone: SCHEDULE_TZ_MSK })
}

/** Начало календарного дня `ymd` (YYYY-MM-DD) в МСК как UTC-instant. */
function mskYmdStartUtc(ymd: string): number {
  return new Date(`${ymd}T00:00:00+03:00`).getTime()
}

/** Разнести смены ShiftLog по календарным дням в МСК (как дата в табеле). */
export function buildShiftSegmentsByUserAndDate(
  logs: { user_id: string; started_at: string; ended_at: string | null }[],
): Record<string, Record<string, ShiftSegment[]>> {
  const out: Record<string, Record<string, ShiftSegment[]>> = {}
  for (const log of logs) {
    if (!log?.user_id || !log.started_at) continue
    const userId = String(log.user_id)
    const start = parseApiDateUtc(log.started_at)
    const end: Date = log.ended_at ? (parseApiDateUtc(log.ended_at) ?? new Date()) : new Date()
    if (!start || Number.isNaN(end.getTime())) continue
    if (!out[userId]) out[userId] = {}
    let currentStart = new Date(start)
    const finalEnd = new Date(end)
    while (currentStart <= finalEnd) {
      const dayKey = mskDateKeyFromUtc(currentStart)
      const nextMskMidnight = mskYmdStartUtc(dayKey) + 24 * 60 * 60 * 1000
      const segmentEnd = new Date(Math.min(finalEnd.getTime(), nextMskMidnight - 1))
      if (!out[userId][dayKey]) out[userId][dayKey] = []
      out[userId][dayKey].push({ start: new Date(currentStart), end: new Date(segmentEnd) })
      currentStart = new Date(nextMskMidnight)
    }
  }
  return out
}

export function buildBreakSegmentsByUserAndDate(
  logs: {
    user_id: string
    break_intervals?: { started_at: string; ended_at: string }[]
  }[],
): Record<string, Record<string, ShiftSegment[]>> {
  const out: Record<string, Record<string, ShiftSegment[]>> = {}
  for (const log of logs) {
    const userId = String(log.user_id)
    const breakIntervals = log.break_intervals || []
    for (const bi of breakIntervals) {
      if (!bi?.started_at || !bi?.ended_at) continue
      const bStart = parseApiDateUtc(bi.started_at)
      const bEnd = parseApiDateUtc(bi.ended_at)
      if (!bStart || !bEnd) continue
      if (!out[userId]) out[userId] = {}
      let bCur = new Date(bStart)
      const bFinal = new Date(bEnd)
      while (bCur <= bFinal) {
        const dayKey = mskDateKeyFromUtc(bCur)
        const nextMskMidnight = mskYmdStartUtc(dayKey) + 24 * 60 * 60 * 1000
        const segEnd = new Date(Math.min(bFinal.getTime(), nextMskMidnight - 1))
        if (!out[userId][dayKey]) out[userId][dayKey] = []
        out[userId][dayKey].push({ start: new Date(bCur), end: new Date(segEnd) })
        bCur = new Date(nextMskMidnight)
      }
    }
  }
  return out
}

export function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

export function getDatesInMonthByWeekdays(
  year: number,
  month: number,
  weekdays: number[],
  excludeDateStr: string,
): string[] {
  if (!weekdays.length) return []
  const set = new Set(weekdays)
  const lastDay = new Date(year, month, 0).getDate()
  const result: string[] = []
  for (let day = 1; day <= lastDay; day++) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    if (dateStr === excludeDateStr) continue
    const d = new Date(year, month - 1, day)
    if (set.has(d.getDay())) result.push(dateStr)
  }
  return result
}

export function validateWorkIntervals(ivs: WorkInterval[]): boolean {
  const sorted = [...ivs].sort((a, b) => a.start_time.localeCompare(b.start_time))
  for (let i = 0; i < sorted.length - 1; i++) {
    if (sorted[i].end_time > sorted[i + 1].start_time) return false
  }
  return true
}
